import {
  Button,
  Checkbox,
  Popconfirm,
  Space,
  Spin,
  Table,
  message,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { useNavigate, useParams } from "react-router-dom";
import { EntityForm } from "../../components/EntityForm";
import { useEffect, useMemo, useState } from "react";
import {
  createWorklog,
  deleteWorklog,
  getWorklogs,
  updateWorklog,
} from "../../api/worklog";
import dayjs from "dayjs";

import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { getTask } from "../../api/task";
import { getFileUrl } from "../../api/file";
import { UserRoles } from "../user/user";

export const WorkLogStatus = {
  Pending: "pending",
  Rejected: "rejected",
  Verified: "verified",
};

export function Worklog({ user }) {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const [task, setTask] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const dataIndex = (page - 1) * perPage;

  const [worklogs, setWorklogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showCreateWorklog, setShowCreateWorklog] = useState(false);
  const [editWorklog, setEditWorklog] = useState(null);

  const sections = useMemo(() => {
    const signatures = [];
    if (editWorklog && editWorklog.signature) {
      signatures.push({
        ...editWorklog.signature,
        uid: editWorklog.signature.id,
        name: editWorklog.signature.originalName,
        status: "done",
        url: getFileUrl(editWorklog.signature),
      });
    }

    const formItems = [
      {
        items: [
          {
            type: "date",
            label: "Start Date",
            name: "startAt",
            showTime: true,
            format: "YYYY-MM-DD HH:mm a",
            required: true,
            col: 12,
          },
        ],
      },
      {
        items: [
          {
            type: "date",
            label: "End Date",
            name: "endAt",
            showTime: true,
            format: "YYYY-MM-DD HH:mm a",
            required: true,
            col: 12,
          },
        ],
      },

      {
        items: [
          {
            type: "avatar",
            label: "Signature (2MB)",
            name: "signature",
            col: 12,
            maxLen: 200,
            fileList: signatures,
          },
        ],
      },
    ];

    if ([UserRoles.Admin, UserRoles.User].includes(user.role)) {
      formItems.push(
        ...[
          {
            items: [
              {
                type: "async-select",
                label: "Status",
                name: "status",
                required: true,
                options: Object.keys(WorkLogStatus).map((status) => {
                  return {
                    value: WorkLogStatus[status],
                    label: status,
                  };
                }),
              },
            ],
          },
          {
            items: [
              {
                type: "number",
                label: "User Rate (Only for special pricing)",
                name: "userRateFrmAdmin",
                required: false,
              },
              {
                type: "number",
                label: "Equipment Rate (Only for special pricing)",
                name: "equipmentRateFrmAdmin",
                required: false,
              },
            ],
          },
        ]
      );
    }

    return formItems;
  }, [editWorklog]);

  const fetchWorklogs = async ({ page, taskId }) => {
    try {
      setIsLoading(false);
      const { total, results } = await getWorklogs({ page, perPage, taskId });
      setTotal(total);
      setWorklogs(results);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!task) return;
    fetchWorklogs({ page, perPage, taskId: task.id });
  }, [page, task]);

  useEffect(() => {
    const fethchTask = async () => {
      try {
        setIsLoading(true);
        const task = await getTask(taskId);
        setTask(task);
        setIsLoading(false);
      } catch (err) {
        message.error("Something went wrong");
        setIsLoading(false);
      }
    };
    fethchTask();
  }, [taskId]);

  const handleSubmit = async ({ ...worklogData }) => {
    if (worklogData.startAt > worklogData.endAt) {
      message.error("Start date must be less than end date");
      throw new Error("Start date must be less than end date");
    }

    const signature =
      worklogData.signature &&
      worklogData.signature.fileList &&
      worklogData.signature.fileList.length
        ? worklogData.signature.fileList.map((file) => {
            if (!file.response) return file;
            return file.response.file;
          })[0]
        : worklogData.signature;

    if (editWorklog) {
      await updateWorklog({
        ...worklogData,
        signature,
        id: editWorklog.id,
        task,
      });
    } else {
      await createWorklog({ ...worklogData, signature, task });
    }
    setEditWorklog(null);
    setShowCreateWorklog(false);
    fetchWorklogs({ page, perPage, taskId: task.id });
  };

  const handleDelete = async (worklog) => {
    await deleteWorklog(worklog.id);
    fetchWorklogs({ page, perPage, taskId: task.id });
  };

  const TableCols = [
    { title: "No", dataIndex: "no", key: "no", width: 80 },
    { title: "Start At", dataIndex: "startAt", key: "startAt" },
    { title: "End At", dataIndex: "endAt", key: "endAt" },
    { title: "Status", dataIndex: "status", key: "status" },
    {
      title: "Action",
      key: "action",
      render: (text, record, index) => (
        <Space size={2}>
          <Popconfirm
            title="Delete the task"
            description="Are you sure to delete this worklog?"
            onConfirm={() => handleDelete(worklogs[index])}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger></Button>
          </Popconfirm>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setEditWorklog(worklogs[index])}
          ></Button>
        </Space>
      ),
    },
  ];

  if (!task) return <Spin></Spin>;

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <PageHeader
          onBack={() => navigate(-1)}
          title={`${task.name}`}
          subTitle="List of worklogs for the task"
        />

        <Button type="primary" onClick={() => setShowCreateWorklog(true)}>
          Create Worklog
        </Button>
      </Space>

      <Table
        dataSource={worklogs.map((worklog, index) => {
          return {
            no: dataIndex + index + 1,
            ...worklog,
            startAt: dayjs(worklog.startAt).format("YYYY-MM-DD HH:mm a"),
            endAt: dayjs(worklog.endAt).format("YYYY-MM-DD HH:mm a"),
            status: Object.keys(WorkLogStatus).find(
              (status) => WorkLogStatus[status] === worklog.status
            ),
          };
        })}
        columns={TableCols}
        loading={isLoading}
        pagination={{
          size: "small",
          total,
          pageSize: 5,
          current: parseInt(page),
          onChange: setPage,
          showQuickJumper: true,
          showTotal: () => `Total ${total} worklogs`,
          position: ["bottomLeft"],
        }}
      />

      {(showCreateWorklog || editWorklog) && (
        <EntityForm
          initialValues={
            editWorklog
              ? {
                  ...editWorklog,
                  startAt: dayjs(editWorklog.startAt, "YYYY-MM-DD HH:mm aZ[Z]"),
                  endAt: dayjs(editWorklog.endAt, "YYYY-MM-DD HH:mm aZ[Z]"),
                }
              : null
          }
          sections={sections}
          title="Worklog"
          open={showCreateWorklog || editWorklog}
          onCancel={() => {
            setShowCreateWorklog(false);
            setEditWorklog(null);
          }}
          onSubmit={handleSubmit}
          onDelete={() => handleDelete(editWorklog).then(() => setEditWorklog(null))}
          isEditMode={!!editWorklog}
        ></EntityForm>
      )}
    </Space>
  );
}
